import { StyleSheet, View } from 'react-native';
import { Text } from '@/components/common/AppText';
import { CheckCircle2, Mail, AlertCircle } from 'lucide-react-native';
import { borderRadius, colors, spacing } from '@tabbio/tokens';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../hooks/useTheme';
import type { LibraryEmailForwardingReadiness } from '../../lib/library/types';

interface Props {
  readiness: LibraryEmailForwardingReadiness;
}

export function LibraryEmailForwardingBanner({ readiness }: Props) {
  const { theme } = useTheme();
  const { t } = useTranslation('common');

  const palette = readinessPalette(readiness, theme);

  return (
    <View
      style={[
        styles.container,
        {
          backgroundColor: palette.background,
          borderColor: palette.border,
        },
      ]}
    >
      <View style={[styles.iconWrap, { backgroundColor: palette.iconBg }]}>
        {readiness === 'active' ? (
          <CheckCircle2 size={18} color={palette.icon} strokeWidth={2} />
        ) : readiness === 'partial' ? (
          <AlertCircle size={18} color={palette.icon} strokeWidth={2} />
        ) : (
          <Mail size={18} color={palette.icon} strokeWidth={2} />
        )}
      </View>
      <View style={styles.body}>
        <Text style={[styles.title, { color: theme.text }]} numberOfLines={1}>
          {t(`library.forwarding.readiness.${readiness}.title`)}
        </Text>
        <Text
          style={[styles.subtitle, { color: theme.textSecondary }]}
          numberOfLines={2}
        >
          {t(`library.forwarding.readiness.${readiness}.subtitle`)}
        </Text>
      </View>
    </View>
  );
}

function readinessPalette(
  readiness: LibraryEmailForwardingReadiness,
  theme: ReturnType<typeof useTheme>['theme'],
) {
  if (readiness === 'active') {
    return {
      background: theme.surface,
      border: theme.borderSubtle,
      iconBg: 'rgba(16, 185, 129, 0.14)',
      icon: colors.success,
    };
  }

  if (readiness === 'partial') {
    return {
      background: theme.surface,
      border: theme.borderSubtle,
      iconBg: 'rgba(245, 158, 11, 0.14)',
      icon: colors.warning,
    };
  }

  return {
    background: theme.surface,
    border: theme.borderSubtle,
    iconBg: 'rgba(94, 41, 255, 0.10)',
    icon: colors.primary,
  };
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[3],
    padding: spacing[3],
    borderRadius: borderRadius.lg,
    borderWidth: 1,
  },
  iconWrap: {
    width: 36,
    height: 36,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
  },
  body: {
    flex: 1,
    gap: 2,
  },
  title: {
    fontSize: 15,
    fontWeight: '600',
  },
  subtitle: {
    fontSize: 13,
    lineHeight: 18,
  },
});
